import { memo } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@mui/material';

import { ChatEnterButton } from './ChatEnterButton';

const getMessageText = (message) =>
    String(message?.text || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const getOffset = (margins, primaryKey, secondaryKey) => {
    const value = margins?.[primaryKey] ?? margins?.[secondaryKey] ?? 10;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 10;
};

export const CollapsedChat = memo(
    ({ message, margins, position, quickMessages, onDismissMessage, onOpen, onQuickMessage }) => {
        const theme = useTheme();
        const isTop = position?.startsWith('top');
        const isLeft = position?.endsWith('left');
        const horizontalOffset = getOffset(margins, isLeft ? 'left' : 'right', isLeft ? 'right' : 'left');
        const verticalOffset = getOffset(margins, isTop ? 'top' : 'bottom', isTop ? 'bottom' : 'top');
        const fontFamily = String(theme.typography.fontFamily || 'Arial, sans-serif').replace(/\s*!important\s*/g, '');

        let parentBody = null;
        try {
            parentBody = window.parent?.document?.body || null;
        } catch {
            parentBody = null;
        }

        const popupContent = parentBody
            ? createPortal(
                  <div
                      style={{
                          position: 'fixed',
                          [isLeft ? 'left' : 'right']: `${horizontalOffset}px`,
                          [isTop ? 'top' : 'bottom']: `${verticalOffset + 76}px`,
                          zIndex: 1000001,
                          display: 'flex',
                          width: `min(320px, calc(100vw - ${horizontalOffset + 16}px))`,
                          flexDirection: 'column',
                          alignItems: isLeft ? 'flex-start' : 'flex-end',
                          gap: '8px',
                          pointerEvents: 'none',
                          fontFamily,
                      }}
                  >
                      {message && (
                          <div
                              role="button"
                              tabIndex={0}
                              onClick={onOpen}
                              onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') onOpen();
                              }}
                              style={{
                                  position: 'relative',
                                  boxSizing: 'border-box',
                                  width: '100%',
                                  padding: '18px 42px 16px 18px',
                                  border: `1px solid ${theme.palette.grey.main}`,
                                  borderRadius: '8px',
                                  color: theme.palette.primary.text,
                                  backgroundColor: theme.palette.primary.background,
                                  boxShadow: '0 8px 28px rgba(0, 0, 0, 0.16)',
                                  cursor: 'pointer',
                                  pointerEvents: 'auto',
                              }}
                          >
                              <button
                                  type="button"
                                  aria-label="Close notification"
                                  onClick={(event) => {
                                      event.stopPropagation();
                                      onDismissMessage();
                                  }}
                                  style={{
                                      position: 'absolute',
                                      top: '8px',
                                      right: '10px',
                                      padding: 0,
                                      border: 0,
                                      color: theme.palette.primary.text,
                                      background: 'transparent',
                                      fontFamily,
                                      fontSize: '24px',
                                      lineHeight: 1,
                                      cursor: 'pointer',
                                  }}
                              >
                                  ×
                              </button>
                              <div
                                  style={{
                                      display: '-webkit-box',
                                      overflow: 'hidden',
                                      WebkitBoxOrient: 'vertical',
                                      WebkitLineClamp: 5,
                                      fontSize: '14px',
                                      lineHeight: 1.4,
                                      whiteSpace: 'pre-line',
                                      wordBreak: 'break-word',
                                  }}
                              >
                                  {getMessageText(message)}
                              </div>
                              {(message?.user?.name || message?.user?.avatar) && (
                                  <div
                                      style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '8px',
                                          marginTop: '10px',
                                      }}
                                  >
                                      {message?.user?.avatar && (
                                          <img
                                              src={message.user.avatar}
                                              alt={message?.user?.name || ''}
                                              width="28"
                                              height="28"
                                              style={{
                                                  display: 'block',
                                                  width: '28px',
                                                  height: '28px',
                                                  minWidth: '28px',
                                                  maxWidth: '28px',
                                                  flex: '0 0 28px',
                                                  borderRadius: '50%',
                                                  objectFit: 'cover',
                                              }}
                                          />
                                      )}
                                      <span style={{ minWidth: 0, overflowWrap: 'anywhere', color: theme.palette.grey.derken, fontSize: '12px' }}>
                                          {message?.user?.name}
                                      </span>
                                  </div>
                              )}
                          </div>
                      )}

                      {quickMessages.map(({ button, message: quickMessage }, index) => (
                          <button
                              type="button"
                              key={`${button}-${index}`}
                              onClick={() => onQuickMessage(quickMessage)}
                              style={{
                                  maxWidth: '100%',
                                  padding: '9px 14px',
                                  border: `1px solid ${theme.palette.header.main}`,
                                  borderRadius: '18px',
                                  color: theme.palette.primary.text,
                                  backgroundColor: theme.palette.primary.background,
                                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
                                  fontFamily,
                                  fontSize: '13px',
                                  lineHeight: 1.25,
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  pointerEvents: 'auto',
                              }}
                          >
                              {button}
                          </button>
                      ))}
                  </div>,
                  parentBody
              )
            : null;

        return (
            <>
                {popupContent}
                <ChatEnterButton onClick={onOpen} />
            </>
        );
    }
);
